import CustomTextArea from "@/components/CustomTextArea";
import Flex from "@/components/Flex";
import Input from "@/components/Input";
import DropZone from "@/components/app/DropZone";
import { useSnackbar } from "@/hooks/useSnackbar";
import useCurrentUser from "@/hooks/local-storage/use-current-user";
import { getPublicRealm } from "@/layout/app/patient-portal/public-view/dob-validation/useValidateDob";
import { useSendEmailWithAttachment } from "@/queries/help-support/use-send-email-with-attachment";
import { getTenantFromHostname } from "@/utils/get-tenant-from-hostname";
import { showToastOnApiCall } from "@/utils/utils";
import { Box, Button } from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  MAIL_US_ATTACHMENT_ACCEPT,
  MAIL_US_ATTACHMENT_CTA,
  MAIL_US_ATTACHMENT_HEIGHT,
  MAIL_US_INITIAL_VALUES,
  MAIL_US_LABEL,
  MAIL_US_MAX_ATTACHMENT_SIZE_BYTES,
  MAIL_US_MESSAGE_MIN_ROWS,
  MAIL_US_PLACEHOLDER,
  MAIL_US_SUBMIT_LABEL,
  MAIL_US_VALIDATION_MESSAGE,
} from "../support-constant";
import type { MailUsFormValues } from "../support-types";

export interface MailUsProps {
  toEmail: string;
  onSuccess: () => void;
}

const MailUs = ({ toEmail, onSuccess }: MailUsProps) => {
  const { mutateAsync: sendEmail } = useSendEmailWithAttachment();
  const showSnackbar = useSnackbar();
  const [me] = useCurrentUser();
  const xTENANTID = getPublicRealm() || getTenantFromHostname();

  const handleSendMail = (values: MailUsFormValues) => {
    showToastOnApiCall({
      apiCall: sendEmail({
        toEmail,
        fromEmail: me?.email ?? "",
        subject: values.subject,
        description: values.description,
        attachment: values.attachment,
        xTENANTID,
      }),
      showSnackbar,
      onSave: onSuccess,
    });
  };

  const formik = useFormik<MailUsFormValues>({
    initialValues: MAIL_US_INITIAL_VALUES,
    validationSchema,
    onSubmit: handleSendMail,
  });

  const subjectError = formik.touched.subject
    ? formik.errors.subject
    : undefined;
  const descriptionError = formik.touched.description
    ? formik.errors.description
    : undefined;
  const attachmentError = formik.touched.attachment
    ? (formik.errors.attachment as string | undefined)
    : undefined;

  return (
    <form onSubmit={formik.handleSubmit}>
      <Flex flexDirection="column" gap={3}>
        <Input
          label={MAIL_US_LABEL.SUBJECT}
          required
          textFieldProps={{
            name: "subject",
            value: formik.values.subject,
            onChange: formik.handleChange,
            onBlur: formik.handleBlur,
            placeholder: MAIL_US_PLACEHOLDER.SUBJECT,
          }}
          error={subjectError}
        />

        <CustomTextArea
          label={MAIL_US_LABEL.MESSAGE_BODY}
          required
          minRows={MAIL_US_MESSAGE_MIN_ROWS}
          name="description"
          value={formik.values.description}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          placeholder={MAIL_US_PLACEHOLDER.MESSAGE_BODY}
          error={Boolean(descriptionError)}
          helperText={descriptionError}
        />

        <Box>
          <DropZone
            height={MAIL_US_ATTACHMENT_HEIGHT}
            accept={MAIL_US_ATTACHMENT_ACCEPT}
            content={MAIL_US_ATTACHMENT_CTA}
            label={MAIL_US_LABEL.ATTACHMENT}
            onClick={(files) => {
              formik.setFieldTouched("attachment", true, false);
              formik.setFieldValue("attachment", files?.item(0) ?? undefined);
            }}
            error={attachmentError}
          />
        </Box>

        <Flex justifyContent="flex-end">
          <Button variant="contained" type="submit">
            {MAIL_US_SUBMIT_LABEL}
          </Button>
        </Flex>
      </Flex>
    </form>
  );
};

export default MailUs;

const validationSchema = Yup.object({
  subject: Yup.string().required(MAIL_US_VALIDATION_MESSAGE.SUBJECT_REQUIRED),
  description: Yup.string().required(
    MAIL_US_VALIDATION_MESSAGE.MESSAGE_REQUIRED
  ),
  attachment: Yup.mixed<File>().test(
    "fileSize",
    MAIL_US_VALIDATION_MESSAGE.ATTACHMENT_SIZE,
    (value) => !value || value.size <= MAIL_US_MAX_ATTACHMENT_SIZE_BYTES
  ),
});
